import { api } from '@/lib/api';

export type LeadStatus =
  | 'NEW'
  | 'CONTACTED'
  | 'INTERESTED'
  | 'FOLLOW_UP'
  | 'CONVERTED'
  | 'LOST'
  | 'CLOSED';

export type LeadSubStatus =
  // NEW
  | 'NOT_CONTACTED'
  | 'AUTO_GENERATED'
  | 'WALK_IN_ENQUIRY'
  | 'REFERRED_LEAD'
  // CONTACTED
  | 'CONNECTED_DETAILS_SHARED'
  | 'CONNECTED_NEEDS_FOLLOW_UP'
  | 'NOT_REACHABLE'
  | 'WRONG_NUMBER'
  | 'SWITCHED_OFF'
  // INTERESTED
  | 'ASKED_FOR_COST'
  | 'ASKED_FOR_DOCTOR_INFO'
  | 'ASKED_FOR_APPOINTMENT'
  | 'WANTS_CALL_BACK'
  | 'COMPARING_OPTIONS'
  // FOLLOW_UP
  | 'FOLLOW_UP_SCHEDULED'
  | 'AWAITING_PATIENT_DECISION'
  | 'AWAITING_REPORTS'
  | 'AWAITING_FAMILY_DISCUSSION'
  | 'RESCHEDULED_CALL'
  | 'NO_RESPONSE_AFTER_FOLLOW_UP'
  // CONVERTED
  | 'OPD_BOOKED'
  | 'OPD_VISITED'
  | 'IPD_ADMITTED'
  | 'PROCEDURE_BOOKED'
  | 'DAY_CARE_DONE'
  // LOST
  | 'PRICE_TOO_HIGH'
  | 'WENT_TO_COMPETITOR'
  | 'NOT_INTERESTED'
  | 'TREATMENT_POSTPONED'
  | 'LOCATION_ISSUE'
  | 'TRUST_ISSUE'
  | 'NO_RESPONSE'
  | 'DUPLICATE_LEAD'
  // CLOSED
  | 'SUCCESSFULLY_TREATED'
  | 'CASE_COMPLETED'
  | 'LONG_TERM_FOLLOW_UP';

export interface Lead {
  id: string;
  hospitalId: string;
  patientName: string;
  phone: string;
  /** Secondary contact number (optional; searchable in list) */
  alternatePhone?: string | null;
  email?: string;
  age?: number;
  gender?: 'MALE' | 'FEMALE' | 'OTHER' | null;
  dob?: string | null;
  profession?: string | null;
  location?: string | null;
  source: string;
  sourceDetails?: string;
  /** Marketing / ad campaign label from integrations or manual entry */
  campaignName?: string | null;
  /** Resolved label for UI (Meta sheet/webhook names when stored name is a placeholder). */
  displayCampaignName?: string | null;
  /** Google Ads gclid when attributed from landing → WhatsApp flow */
  gclid?: string | null;
  gbraid?: string | null;
  wbraid?: string | null;
  fbclid?: string | null;
  /** UTM campaign string from landing click (column `utm_campaign`) */
  googleAdsUtmCampaign?: string | null;
  procedureInterest?: string | null;
  landingPageUrl?: string | null;
  specialisation?: string | null;
  specialisationRemarks?: string | null;
  /** null = not specified */
  isSurgeryCase?: boolean | null;
  /** Aesthetic procedure of interest when this is a surgery case */
  aestheticProcedureName?: string | null;
  /** Optional tentative surgery date (YYYY-MM-DD shown in UI) */
  tentativeSurgeryDate?: string | null;
  /** Person reaching out or coordinating for the patient */
  contactPersonName?: string | null;
  /** Free text, e.g. Husband, Wife, Friend */
  contactRelationship?: string | null;
  leadSubStatus?: LeadSubStatus | null;
  /** Set when the same phone re-enquires; shown under NEW on the leads list. */
  priorLeadStatus?: LeadStatus | null;
  priorLeadSubStatus?: LeadSubStatus | null;
  status: LeadStatus;
  assignedToId?: string;
  notes?: string;
  lastContactedAt?: string;
  lastInboundWhatsAppAt?: string | null;
  lastInboundTouchAt?: string | null;
  nextFollowUpAt?: string;
  createdAt: string;
  updatedAt: string;
  aiPriorityScore?: number;
  aiPriorityLabel?: 'HIGH' | 'MEDIUM' | 'LOW';
  aiLostReasonCategory?: string;
  aiLostReasonSummary?: string;
  aiConfidence?: number;
  aiExplanation?: string;
  /** Optional BANT scoring fields (additive) */
  budgetScore?: number | null;
  authorityScore?: number | null;
  needScore?: number | null;
  timelineScore?: number | null;
  weightedScore?: number | null;
  bantTier?: 'Hot' | 'Warm' | 'Cold' | 'Bad' | null;
  bantProfileComplete?: boolean;
  budgetWasWontReveal?: boolean;
  scoreLastUpdatedAt?: string | null;
  scoreLastUpdatedBy?: { id: string; name: string } | null;
  assignedTo?: { id: string; name: string };
  /** IPD Cases sidebar: patient has paid OPD consultation bill (matched by phone) */
  opdConsultationPaid?: boolean;
  /** IPD Cases: ₹0 consult or CRM FREE tier OPD (show "Free OPD" in Paid column) */
  opdConsultationFree?: boolean;
  /** IPD Cases: latest paid OPD consultation bill id (for opening PDF) */
  opdConsultationBillId?: string | null;
  /** IPD Cases list: campaignName with fallback to UTM campaign */
  campaignLabel?: string | null;
  /** IPD Cases: bill date when paid OPD, else latest OPD appointment */
  opdDisplayAt?: string | null;
  /** First OPD visited timestamp (CRM mark or OPD bill); retained after stage changes */
  opdVisitedAt?: string | null;
  /** IPD Cases: doctor from latest OPD appointment (when patient matched by phone) */
  opdDoctorName?: string | null;
  /** IPD Cases: CRM lead hospital unit name */
  hospitalName?: string | null;
  /** IPD Cases: hospital id where OPD visit/bill occurred (may differ from lead hospital in a chain) */
  opdHospitalId?: string | null;
  /** IPD Cases: hospital where OPD visit occurred (may differ from lead hospital in a chain) */
  opdHospitalName?: string | null;
  /** PRACTO source leads: doctor from Practo appointment / notes / OPD match */
  practoDoctorName?: string | null;
  /** Same as updatedAt; explicit for IPD table */
  leadUpdatedAt?: string;
  ingestedViaApiKey?: { id: string; name: string; keyPrefix?: string };
  /** Not-reachable call cadence (manual clicks; max 3/day IST, 9 total; any status/sub-status). */
  notReachableAttemptTotal?: number;
  notReachableAttemptDayKey?: string | null;
  notReachableAttemptsToday?: number;
  /** Customer picked callback time via not-reachable DNP Reschedule WhatsApp Flow */
  customerCallbackAt?: string | null;
  /** Customer tapped Call Now Please on follow_up_missed template */
  callNowRequestedAt?: string | null;
  /** Customer replied to lost_check — pinned to top for immediate call */
  lostRevivalInboundAt?: string | null;
  /** Open lead with no sales-person touch past FRT SLA (15 office min) */
  frtOverdue?: boolean;
  /** Auto WhatsApp campaign welcome sent (Google/Meta paid leads) */
  campaignWelcomeWaSent?: boolean;
  campaignWelcomeWaSentAt?: string | null;
  /** Website public OPD booking linked to this lead */
  websiteOpdAppointmentId?: string | null;
  websiteOpdBookedAt?: string | null;
  websiteOpdDoctorName?: string | null;
  websiteOpdHospitalName?: string | null;
  websiteOpdScheduledAt?: string | null;
  /** CAMPAIGN_WHATSAPP | DIRECT_WEBSITE — how patient self-booked OPD */
  websiteOpdBookingChannel?: string | null;
}

/** CRM IPD Admitted tab — one row per IPD admission linked to a lead (phone match). */
export type CrmIpdAdmittedRow = Lead & {
  leadId: string;
  admissionId: string;
  ipdStatus: 'ADMITTED' | 'DISCHARGED' | string;
  admissionDate: string;
  dischargeDate?: string | null;
  ipdNumber?: string | null;
  surgeryName?: string | null;
  ipdDoctorName?: string | null;
  ipdHospitalName?: string | null;
  /** CRM lead hospital (owner attribution site). */
  leadHospitalName?: string | null;
};

export type LeadFrtSalesPersonRow = {
  staffId: string | null;
  name: string;
  totalLeads: number;
  responded: number;
  pending: number;
  overdue: number;
  avgFrtMinutes: number | null;
  medianFrtMinutes: number | null;
  withinSla: number;
  withinSlaPct: number | null;
};

export type LeadFrtLeadRow = {
  leadId: string;
  patientName: string;
  phone: string;
  status: LeadStatus;
  createdAt: string;
  firstResponseAt: string | null;
  frtMinutes: number | null;
  overdue: boolean;
  assignedToId: string | null;
  assignedToName: string;
};

export type LeadFrtWeeklyPoint = {
  weekKey: string;
  weekLabel: string;
  totalAvgFrtMinutes: number | null;
  totalResponded: number;
  bySalesPerson: Array<{
    staffId: string | null;
    name: string;
    avgFrtMinutes: number | null;
    responded: number;
  }>;
};

export type LeadTalkTimePersonRow = {
  staffId: string | null;
  name: string;
  exotelCalls: number;
  exotelSeconds: number;
  appCalls: number;
  appSeconds: number;
  totalCalls: number;
  totalSeconds: number;
};

export type LeadTalkTimeDayRow = {
  date: string;
  exotelSeconds: number;
  appSeconds: number;
  totalSeconds: number;
};

export type LeadTalkTimeReport = {
  period?: { from: string; to: string };
  timezone: 'Asia/Kolkata';
  bySalesPerson: LeadTalkTimePersonRow[];
  byDay: LeadTalkTimeDayRow[];
  summary: {
    exotelCalls: number;
    exotelSeconds: number;
    appCalls: number;
    appSeconds: number;
    totalCalls: number;
    totalSeconds: number;
  };
};

export type LeadTalkTimeLogRow = {
  id: string;
  channel: 'EXOTEL' | 'SALES_APP';
  callAt: string;
  durationSec: number;
  patientName: string;
  phone: string;
  salesPersonId: string | null;
  salesPersonName: string;
  leadId: string;
  leadSource: string | null;
  campaignName: string | null;
  hasRecording: boolean;
  recordingId: string | null;
  activityId: string | null;
  exotelRecordingUrl: string | null;
};

export type LeadTalkTimeLogsResponse = {
  period?: { from: string; to: string };
  timezone: 'Asia/Kolkata';
  rows: LeadTalkTimeLogRow[];
  total: number;
  hasMore: boolean;
};

export type LeadFrtReport = {
  period?: { from: string; to: string };
  slaMinutes: number;
  /** Office window used for FRT (e.g. 10:00–19:00 IST). */
  officeHoursLabel?: string;
  summary: Omit<LeadFrtSalesPersonRow, 'staffId' | 'name'>;
  bySalesPerson: LeadFrtSalesPersonRow[];
  /** Week-on-week avg FRT (total + per active sales person). */
  weeklyAvgFrt?: LeadFrtWeeklyPoint[];
  leads: LeadFrtLeadRow[];
};

export type LeadPerformanceDrillMetric =
  | 'leads'
  | 'warm'
  | 'dnps'
  | 'lost'
  | 'open_pipeline'
  | 'opds'
  | 'total_opds'
  | 'ipds'
  | 'total_ipds'
  | 'opds_still_open'
  | 'opds_lost';

export type LeadPerformanceDrillRow = {
  leadId: string;
  patientName: string;
  phone: string;
  source: string;
  status: string;
  leadSubStatus: string | null;
  campaignName: string | null;
  assigneeName: string | null;
};

export type OpdReportDrillMetric =
  | 'booked_online_free'
  | 'booked_online_paid'
  | 'booked_offline_free'
  | 'booked_offline_paid'
  | 'visited_online_free'
  | 'visited_online_paid'
  | 'visited_offline_free'
  | 'visited_offline_paid'
  | 'status_online_not_visited'
  | 'status_offline_not_visited'
  | 'status_online_rescheduled'
  | 'status_offline_rescheduled'
  | 'status_online_lost'
  | 'status_offline_lost';

export type OpdReportFreePaid = { free: number; paid: number };
export type OpdReportModeSplit = { online: OpdReportFreePaid; offline: OpdReportFreePaid };
export type OpdReportStatusSplit = {
  online: { notVisited: number; rescheduled: number; lost: number };
  offline: { notVisited: number; rescheduled: number; lost: number };
};

export type OpdReportRepRow = {
  staffId: string;
  staffName: string;
  booked: OpdReportModeSplit;
  visited: OpdReportModeSplit;
  status: OpdReportStatusSplit;
};

export type OpdReportDrillRow = {
  leadId: string;
  appointmentId: string | null;
  patientName: string;
  phone: string;
  source: string;
  leadStatus: string;
  leadSubStatus: string | null;
  staffId: string | null;
  staffName: string | null;
  mode: string;
  billedValue: number;
  bookedAt: string | null;
  visitedAt: string | null;
};

export type LeadOpdReport = {
  from: string;
  to: string;
  sources: string[] | null;
  bySalesPerson: OpdReportRepRow[];
  totals: {
    booked: OpdReportModeSplit;
    visited: OpdReportModeSplit;
    status: OpdReportStatusSplit;
  };
};

export type SarvamOpdPerformanceRow = {
  staffId: string;
  staffName: string;
  opdBooked: number;
  opdVisitedFree: number;
  opdVisitedPaid: number;
  opdBillValue: number;
  ipdDone: number;
  ipdDischargeBillValue: number;
};

export type SarvamOpdPerformanceReport = {
  from: string;
  to: string;
  rows: SarvamOpdPerformanceRow[];
  totals: SarvamOpdPerformanceRow & { staffName: "Total" };
};

export type OrganicConversionRow = {
  day: string;
  staffId: string;
  staffName: string;
  leadsCreated: number;
  opdVisited: number;
  ipdDischarged: number;
  ipdBillAmount: number;
};

export type OrganicLeadConversionsReport = {
  from: string;
  to: string;
  sources: string[];
  rows: OrganicConversionRow[];
  totals: {
    leadsCreated: number;
    opdVisited: number;
    ipdDischarged: number;
    ipdBillAmount: number;
  };
};

/** PATCH /leads/:id — fields may be sent as `null` to clear */
export type LeadUpdatePayload = {
  [K in keyof Pick<
    Lead,
    | 'patientName'
    | 'phone'
    | 'alternatePhone'
    | 'email'
    | 'age'
    | 'gender'
    | 'dob'
    | 'profession'
    | 'location'
    | 'status'
    | 'assignedToId'
    | 'notes'
    | 'lastContactedAt'
    | 'nextFollowUpAt'
    | 'specialisation'
    | 'specialisationRemarks'
    | 'isSurgeryCase'
    | 'tentativeSurgeryDate'
    | 'aestheticProcedureName'
    | 'contactPersonName'
    | 'contactRelationship'
    | 'leadSubStatus'
    | 'campaignName'
    | 'source'
  >]?: Pick<
    Lead,
    | 'patientName'
    | 'phone'
    | 'alternatePhone'
    | 'email'
    | 'age'
    | 'gender'
    | 'dob'
    | 'profession'
    | 'location'
    | 'status'
    | 'assignedToId'
    | 'notes'
    | 'lastContactedAt'
    | 'nextFollowUpAt'
    | 'specialisation'
    | 'specialisationRemarks'
    | 'isSurgeryCase'
    | 'tentativeSurgeryDate'
    | 'aestheticProcedureName'
    | 'contactPersonName'
    | 'contactRelationship'
    | 'leadSubStatus'
    | 'campaignName'
    | 'source'
  >[K] | null;
} & {
  assignmentReason?: string;
};

export interface LeadAssignmentAuditRow {
  id: string;
  changedAt: string;
  reason: string | null;
  leadId: string;
  patientName: string;
  phone: string;
  hospitalId: string;
  hospitalName: string;
  fromStaffId: string | null;
  fromStaffName: string | null;
  toStaffId: string | null;
  toStaffName: string | null;
  changedById: string | null;
  changedByName: string | null;
}

export interface LeadAssignmentAuditResult {
  rows: LeadAssignmentAuditRow[];
  total: number;
}

export interface LeadActivity {
  id: string;
  leadId: string;
  type: string;
  summary?: string;
  createdAt: string;
  createdBy?: { id: string; name: string };
  /** Populated when type is ATTACHMENT (patient pic, document, etc.) */
  attachmentUrl?: string | null;
  attachmentFilename?: string | null;
  attachmentMime?: string | null;
}

export interface PaginatedLeads {
  items: Lead[];
  total: number;
  page: number;
  pageSize: number;
}

export interface LeadFilterOptions {
  sources: string[];
  agencyKeys: { id: string; name: string }[];
  campaignNames: string[];
}

export type CrmTimelineEntry =
  | {
      kind: 'STATUS';
      at: string;
      leadId: string;
      fromStatus: LeadStatus | null;
      toStatus: LeadStatus;
      changedBy?: { id: string; name: string } | null;
      note?: string | null;
    }
  | {
      kind: 'OWNER';
      at: string;
      leadId: string;
      fromStaff?: { id: string; name: string } | null;
      toStaff?: { id: string; name: string } | null;
      changedBy?: { id: string; name: string } | null;
      reason?: string | null;
    }
  | {
      kind: 'ACTIVITY';
      id: string;
      at: string;
      leadId: string;
      activityType: string;
      summary?: string | null;
      createdBy?: { id: string; name: string } | null;
      attachmentUrl?: string | null;
      attachmentFilename?: string | null;
      attachmentMime?: string | null;
    };

export type LeadWhatsAppDirection = 'INBOUND' | 'OUTBOUND';

export interface LeadWhatsAppMessageRow {
  id: string;
  leadId: string;
  hospitalId: string;
  direction: LeadWhatsAppDirection;
  body: string;
  attachmentUrl?: string | null;
  attachmentFilename?: string | null;
  attachmentMime?: string | null;
  waMessageId?: string | null;
  createdAt: string;
  createdBy?: { id: string; name: string } | null;
}

export interface LeadWhatsAppMeta {
  optedOut: boolean;
  lastInboundAt: string | null;
  freeformUntil: string | null;
  canUseFreeform: boolean;
}

export interface AppCallRecordingRow {
  id: string;
  activityId: string | null;
  durationSec: number | null;
  createdAt: string;
  attachmentMime?: string | null;
  staff?: { id: string; name: string } | null;
}

export type LeadDetail = Lead & {
  activities: LeadActivity[];
  appCallRecordings?: AppCallRecordingRow[];
  clusterLeadIds?: string[];
  crmTimeline?: CrmTimelineEntry[];
  whatsappMessages?: LeadWhatsAppMessageRow[];
  whatsappMeta?: LeadWhatsAppMeta;
  /** Latest OPD bill date (appointment-linked) for phone-matched patient. */
  opdVisitedAt?: string | null;
};

export interface BulkImportResult {
  created: number;
  failed: Array<{ row: number; reason: string }>;
  rowCount: number;
  leadIds: string[];
}

export const leadsApi = {
  listLeads: (params?: {
    /** Comma-separated LeadStatus values */
    status?: string;
    /** Comma-separated sub-status values; include __none__ for NULL */
    leadSubStatus?: string;
    sortBy?:
      | 'createdAt'
      | 'updatedAt'
      | 'patientName'
      | 'phone'
      | 'source'
      | 'campaignName'
      | 'status'
      | 'aiPriorityScore'
      | 'assignedToId'
      | 'nextFollowUpAt'
      | 'specialisation'
      | 'isSurgeryCase'
      | 'ingestedViaApiKeyId'
      | 'lastInboundWhatsAppAt';
    sortDir?: 'asc' | 'desc';
    /** Comma-separated source values */
    source?: string;
    /** Comma-separated campaign names; include __none__ for leads with no campaign */
    campaign?: string;
    assignedToId?: string;
    search?: string;
    from?: string;
    to?: string;
    /** ISO range on nextFollowUpAt (IST day boundaries from LeadsPage) */
    followUpFrom?: string;
    followUpTo?: string;
    page?: number;
    pageSize?: number;
    hospitalId?: string;
    /** UUID of agency ingest key, or __none__ for leads with no agency key */
    ingestedViaApiKeyId?: string;
    /** Filter by surgery case flag */
    isSurgeryCase?: 'true' | 'false' | 'unset';
    /** Comma-separated BANT tiers (Hot,Warm,Cold,Bad) or __unscored__ */
    bantTier?: string;
    /** Leads with at least one Sarvam voice call attempt */
    sarvamCalled?: 'true' | 'false';
  }) =>
    api.get<{ message: string; data: PaginatedLeads }>('/api/leads', { params }),

  listInboundWhatsApp: (params?: { since?: string }) =>
    api.get<{
      message: string;
      data: { items: Array<{ id: string; leadId: string; body: string; createdAt: string; lead: any }> };
    }>('/api/leads/whatsapp/inbound', { params }),

  listSidebarIpdCases: (params?: {
    limit?: number;
    page?: number;
    pageSize?: number;
    hospitalId?: string;
    search?: string;
    ownerId?: string;
    doctor?: string;
    sources?: string;
    campaigns?: string;
    opdFrom?: string;
    opdTo?: string;
    surgeryFrom?: string;
    surgeryTo?: string;
    updatedFrom?: string;
    updatedTo?: string;
  }) =>
    api.get<{ message: string; data: { items: Lead[]; total: number; page: number; pageSize: number } }>(
      '/api/leads/sidebar/ipd-cases',
      { params }
    ),

  listSidebarIpdAdmitted: (params?: { limit?: number; hospitalId?: string }) =>
    api.get<{ message: string; data: { items: CrmIpdAdmittedRow[] } }>(
      '/api/leads/sidebar/ipd-admitted',
      { params },
    ),

  getLeadFilterOptions: (params?: { hospitalId?: string }) =>
    api
      .get<{ message: string; data: LeadFilterOptions }>(`/api/leads/filter-options`, { params })
      .then((res) => res.data.data),

  downloadBulkImportTemplate: () =>
    api.get(`/api/leads/import/template`, { responseType: 'blob' }),

  bulkImportLeads: (file: File, superAdminHospitalId?: string) => {
    const fd = new FormData();
    fd.append('file', file);
    if (superAdminHospitalId?.trim()) {
      fd.append('hospitalId', superAdminHospitalId.trim());
    }
    return api.post<{ message: string; data: BulkImportResult }>(`/api/leads/import/bulk`, fd);
  },
  sendLeadWhatsAppMedia: (
    leadId: string,
    payload: {
      file: File;
      caption?: string;
      templateName?: string;
      templateLanguage?: string;
      templateBodyParams?: string[];
    }
  ) => {
    const fd = new FormData();
    fd.append('file', payload.file);
    if (payload.caption) fd.append('caption', payload.caption);
    if (payload.templateName) fd.append('templateName', payload.templateName);
    if (payload.templateLanguage) fd.append('templateLanguage', payload.templateLanguage);
    if (payload.templateBodyParams) {
      // Keep server-side parsing consistent with existing JSON API.
      fd.append('templateBodyParams', JSON.stringify(payload.templateBodyParams));
    }
    return api.post(`/api/leads/${leadId}/whatsapp/send-media`, fd);
  },

  createLead: (data: {
    patientName: string;
    phone: string;
    email?: string;
    age?: number;
    gender?: 'MALE' | 'FEMALE' | 'OTHER' | null;
    dob?: string | null;
    profession?: string | null;
    location?: string | null;
    source: string;
    sourceDetails?: string;
    campaignName?: string | null;
    notes?: string;
    alternatePhone?: string | null;
    contactPersonName?: string | null;
    contactRelationship?: string | null;
    aestheticProcedureName?: string | null;
    assignedToId?: string;
    nextFollowUpAt?: string;
    hospitalId?: string;
    specialisation?: string | null;
    specialisationRemarks?: string | null;
    isSurgeryCase?: boolean | null;
    tentativeSurgeryDate?: string | null;
    leadSubStatus?: LeadSubStatus | null;
  }) => api.post<{ message: string; data: Lead }>('/api/leads', data),

  getLeadById: (id: string) =>
    api.get<{ message: string; data: LeadDetail }>(`/api/leads/${id}`),

  getActivityAttachmentPlayUrl: (activityId: string) =>
    api
      .get<{
        message: string;
        data: { url: string; mime?: string | null; filename?: string | null; expiresInSeconds?: number };
      }>(`/api/leads/activities/${activityId}/attachment-url`)
      .then((r) => r.data.data),

  /** Desktop CRM → Sales mobile app dialer + logged call session. */
  requestRemoteCall: (leadId: string) =>
    api
      .post<{
        message: string;
        data: { leadId: string; patientName: string; phone: string; expiresAt: string };
      }>(`/api/leads/${leadId}/remote-call`)
      .then((r) => r.data.data),

  getCallRecordingPlayUrl: (recordingId: string) =>
    api
      .get<{
        message: string;
        data: {
          url: string;
          mime?: string | null;
          filename?: string | null;
          activityId?: string | null;
          expiresInSeconds?: number;
        };
      }>(`/api/leads/call-recordings/${recordingId}/attachment-url`)
      .then((r) => r.data.data),

  /** Authenticated stream — use blob URL in CRM audio player (chain admin + Exotel/S3). */
  fetchCallRecordingPlaybackBlob: (recordingId: string) =>
    api.get<Blob>(`/api/leads/call-recordings/${recordingId}/stream`, {
      responseType: 'blob',
    }),

  fetchActivityAttachmentPlaybackBlob: (activityId: string) =>
    api.get<Blob>(`/api/leads/activities/${activityId}/attachment/stream`, {
      responseType: 'blob',
    }),

  fetchExotelRecordingPlaybackBlob: (activityId: string) =>
    api.get<Blob>(`/api/leads/activities/${activityId}/exotel-recording/stream`, {
      responseType: 'blob',
    }),

  deleteCallRecording: (recordingId: string) =>
    api.delete<{ message: string; data: { id: string; leadId: string } }>(
      `/api/leads/call-recordings/${recordingId}`
    ),

  sendLeadWhatsApp: (
    id: string,
    body: {
      text?: string;
      templateName?: string;
      templateLanguage?: string;
      templateBodyParams?: string[];
    }
  ) =>
    api.post<{ message: string; data: { waMessageId: string | null; usedTemplate: boolean } }>(
      `/api/leads/${id}/whatsapp/send`,
      body
    ),

  updateLead: (id: string, data: LeadUpdatePayload) =>
    api.patch<{ message: string; data: Lead }>(`/api/leads/${id}`, data),

  deleteLead: (id: string) =>
    api.delete<{ message: string; data: { id: string } }>(`/api/leads/${id}`),

  assignLead: (id: string, assignedToId: string | null, assignmentReason: string) =>
    api.patch<{ message: string; data: Lead }>(`/api/leads/${id}/assign`, {
      assignedToId,
      assignmentReason,
    }),

  bulkAssign: (
    leadIds: string[],
    assignedToId: string | null,
    assignmentReason: string
  ) =>
    api.post<{ message: string; data: { requested: number; found: number; updated: number; missing: string[] } }>(
      `/api/leads/bulk-assign`,
      { leadIds, assignedToId, assignmentReason }
    ),

  listAssignmentAudit: (params?: {
    page?: number;
    pageSize?: number;
    from?: string;
    to?: string;
    staffId?: string;
    search?: string;
    hospitalId?: string;
  }) =>
    api.get<{ message: string; data: LeadAssignmentAuditResult }>(`/api/leads/assignment-audit`, {
      params,
    }),

  addActivity: (id: string, data: { type: string; summary?: string }) =>
    api.post<{ message: string; data: LeadActivity }>(`/api/leads/${id}/activities`, data),

  updateActivity: (activityId: string, data: { type?: string; summary?: string }) =>
    api.patch<{ message: string; data: LeadActivity }>(`/api/leads/activities/${activityId}`, data),

  deleteActivity: (activityId: string) =>
    api.delete<{ message: string; data: { id: string } }>(`/api/leads/activities/${activityId}`),

  incrementNotReachableCall: (id: string) =>
    api.post<{
      message: string;
      data: {
        lead: Lead;
        notReachableAttemptTotal: number;
        notReachableAttemptsToday: number;
        notReachableAttemptDayKey: string;
        autoLost: boolean;
      };
    }>(`/api/leads/${id}/not-reachable-call`, {}),

  uploadLeadActivityFile: (id: string, file: File, summary?: string) => {
    const fd = new FormData();
    fd.append('file', file);
    if (summary?.trim()) {
      fd.append('summary', summary.trim());
    }
    return api.post<{ message: string; data: LeadActivity }>(
      `/api/leads/${id}/activities/upload`,
      fd,
      { timeout: 120000 }
    );
  },

  downloadLeadDump: (params?: { hospitalId?: string; from?: string; to?: string }) =>
    api.get(`/api/leads/export/dump`, {
      params,
      responseType: 'blob',
    }),

  downloadLostReasonsCsv: (params?: { from?: string; to?: string; hospitalId?: string }) =>
    api.get(`/api/leads/export/lost-reasons`, { params, responseType: 'blob' }),

  downloadMissedFollowUpsCsv: (params?: { assignedToId?: string; hospitalId?: string }) =>
    api.get(`/api/leads/export/missed-follow-ups`, { params, responseType: 'blob' }),

  downloadDnpMissedCsv: (params?: { assignedToId?: string; hospitalId?: string }) =>
    api.get(`/api/leads/export/dnp-missed`, { params, responseType: 'blob' }),

  getTodayFollowUps: (hospitalId?: string) =>
    api.get<{ message: string; data: Lead[] }>(`/api/leads/follow-ups/today`, {
      params: hospitalId ? { hospitalId } : undefined,
    }),

  getUpcomingFollowUps: (hospitalId?: string) =>
    api.get<{ message: string; data: Lead[] }>(`/api/leads/follow-ups/upcoming`, {
      params: hospitalId ? { hospitalId } : undefined,
    }),

  getPendingCallNowLeads: () =>
    api.get<{ message: string; data: Lead[] }>(`/api/leads/call-now/pending`),

  acknowledgeCallNow: (leadId: string) =>
    api.post<{ message: string; data: unknown }>(`/api/leads/call-now/acknowledge`, { leadId }),

  dismissFollowUpToday: (leadId: string) =>
    api.post<{ message: string; data: unknown }>(`/api/leads/follow-ups/dismiss`, { leadId }),

  snoozeFollowUp: (leadId: string, hours?: number) =>
    api.post<{ message: string; data: unknown }>(`/api/leads/follow-ups/snooze`, { leadId, hours }),

  getMovementReport: (params?: { from?: string; to?: string; hospitalId?: string }) =>
    api.get<{ message: string; data: any }>(`/api/leads/reports/movement`, { params }),

  getLostReasonsReport: (params?: { from?: string; to?: string; hospitalId?: string }) =>
    api.get<{ message: string; data: any }>(`/api/leads/reports/lost-reasons`, { params }),

  getOpdVisitedOutcomeReport: (params?: { from?: string; to?: string; hospitalId?: string }) =>
    api.get<{ message: string; data: any }>(`/api/leads/reports/opd-visited-outcomes`, { params }),

  downloadOpdVisitedOutcomeReport: (params?: { from?: string; to?: string; hospitalId?: string }) =>
    api.get(`/api/leads/reports/opd-visited-outcomes/export`, { params, responseType: 'blob' }),

  getSelfBookedLeadsReport: (params?: { from?: string; to?: string; hospitalId?: string }) =>
    api.get<{ message: string; data: any }>(`/api/leads/reports/self-booked`, { params }),

  downloadSelfBookedLeadsReport: (params?: { from?: string; to?: string; hospitalId?: string }) =>
    api.get(`/api/leads/reports/self-booked/export`, { params, responseType: 'blob' }),

  getLeadPerformanceReport: (params?: Record<string, string | undefined>) =>
    api.get<{ message: string; data: any }>(`/api/leads/reports/performance`, { params }),

  getLeadPerformanceDrill: (params: {
    from?: string;
    to?: string;
    hospitalId?: string;
    source?: string;
    campaignName?: string;
    assignedToId?: string;
    periodStart: string;
    periodEnd: string;
    metric: LeadPerformanceDrillMetric;
    campaignRow?: string;
  }) =>
    api.get<{ message: string; data: { items: LeadPerformanceDrillRow[] } }>(
      `/api/leads/reports/performance/drill`,
      { params },
    ),

  getCampaignRecommendationsReport: (params?: { hospitalId?: string }) =>
    api.get<{ message: string; data: any }>(`/api/leads/reports/campaign-recommendations`, { params }),

  getOpdReport: (params?: {
    from?: string;
    to?: string;
    hospitalId?: string;
    source?: string;
  }) => api.get<{ message: string; data: LeadOpdReport }>(`/api/leads/reports/opd`, { params }),

  getSarvamOpdPerformanceReport: (params?: { from?: string; to?: string; hospitalId?: string }) =>
    api.get<{ message: string; data: SarvamOpdPerformanceReport }>(
      `/api/leads/reports/sarvam-opd-performance`,
      { params }
    ),

  getOrganicConversionsReport: (params?: { from?: string; to?: string; hospitalId?: string }) =>
    api.get<{ message: string; data: OrganicLeadConversionsReport }>(
      `/api/leads/reports/organic-conversions`,
      { params }
    ),

  getOpdReportDrill: (params: {
    from?: string;
    to?: string;
    hospitalId?: string;
    source?: string;
    metric: OpdReportDrillMetric;
    staffId?: string;
  }) => api.get<{ message: string; data: { items: OpdReportDrillRow[] } }>(`/api/leads/reports/opd/drill`, { params }),

  getOpenAgeingReport: (params?: {
    from?: string;
    to?: string;
    hospitalId?: string;
    assignedToId?: string;
    source?: string;
  }) => api.get<{ message: string; data: any }>(`/api/leads/reports/open-ageing`, { params }),

  downloadOpenAgeingReport: (params?: {
    from?: string;
    to?: string;
    hospitalId?: string;
    assignedToId?: string;
    source?: string;
  }) => api.get(`/api/leads/reports/open-ageing/export`, { params, responseType: 'blob' }),

  getTalkTimeReport: (params?: { from?: string; to?: string; hospitalId?: string }) =>
    api.get<{ message: string; data: LeadTalkTimeReport }>(`/api/leads/reports/talk-time`, {
      params,
    }),

  getTalkTimeLogs: (params?: {
    from?: string;
    to?: string;
    hospitalId?: string;
    staffId?: string;
    source?: string;
    campaignName?: string;
    limit?: number;
    offset?: number;
    sort?: 'callAt_desc' | 'callAt_asc' | 'duration_desc' | 'duration_asc';
  }) =>
    api.get<{ message: string; data: LeadTalkTimeLogsResponse }>(`/api/leads/reports/talk-time/logs`, {
      params,
    }),

  getFrtReport: (params?: { from?: string; to?: string; hospitalId?: string; assignedToId?: string }) =>
    api.get<{ message: string; data: LeadFrtReport }>(`/api/leads/reports/frt`, { params }),

  downloadFrtReport: (params?: { from?: string; to?: string; hospitalId?: string; assignedToId?: string }) =>
    api.get(`/api/leads/reports/frt/export`, { params, responseType: 'blob' }),

  downloadLeadPerformanceReport: (params?: Record<string, string | undefined>) =>
    api.get(`/api/leads/reports/performance/export`, { params, responseType: 'blob' }),

  upsertLeadAdSpend: (body: {
    spendDate: string;
    spendEnd?: string;
    amount: number;
    source?: string;
    campaignName?: string;
    hospitalId?: string;
  }) => api.put<{ message: string; data: any }>(`/api/leads/reports/ad-spend`, body),

  getTransitionReport: (params?: { from?: string; to?: string; hospitalId?: string }) =>
    api.get<{ message: string; data: any }>(`/api/leads/reports/transitions`, { params }),

  downloadTransitionReport: (params: {
    from?: string;
    to?: string;
    hospitalId?: string;
    edge: string;
  }) => api.get(`/api/leads/reports/transitions/export`, { params, responseType: "blob" }),

  getCrmLeadRevenueReport: (params?: { from?: string; to?: string; hospitalId?: string }) =>
    api.get<{ message: string; data: any }>(`/api/leads/reports/crm-revenue`, { params }),

  downloadCrmLeadRevenueDump: (params?: { from?: string; to?: string; hospitalId?: string }) =>
    api.get(`/api/leads/reports/crm-revenue/export`, { params, responseType: 'blob' }),

  getBenchmarkReport: (params?: { from?: string; to?: string }) =>
    api.get<{ message: string; data: any }>(`/api/leads/reports/benchmark`, { params }),

  getBantMeta: () => api.get<{ message: string; data: any }>(`/api/leads/bant/meta`),

  getLeadBantScore: (id: string) =>
    api.get<{ message: string; data: any }>(`/api/leads/${id}/bant-score`),

  upsertLeadBantScore: (
    id: string,
    data: {
      budgetScore?: number | null;
      authorityScore?: number | null;
      needScore?: number | null;
      timelineScore?: number | null;
      budgetWasWontReveal?: boolean;
    }
  ) => api.put<{ message: string; data: any }>(`/api/leads/${id}/bant-score`, data),

  getBantScoringReport: (params?: {
    from?: string;
    to?: string;
    source?: string;
    campaignName?: string;
    hospitalId?: string;
  }) => api.get<{ message: string; data: any }>(`/api/leads/reports/bant-scoring`, { params }),

  getManagerOverview: (params?: { from?: string; to?: string; hospitalId?: string }) =>
    api.get<{ message: string; data: any }>(`/api/leads/manager/overview`, { params }),

  getManagerRepMetrics: (params?: { from?: string; to?: string; hospitalId?: string }) =>
    api.get<{ message: string; data: any }>(`/api/leads/manager/rep-metrics`, { params }),

  getManagerRepMetricLeads: (params: {
    metric: string;
    repId: string;
    from: string;
    to: string;
    hospitalId?: string;
  }) =>
    api.get<{ message: string; data: { items: Lead[] } }>(
      `/api/leads/manager/rep-metrics/leads`,
      { params },
    ),

  getManagerAlerts: (params?: { hospitalId?: string }) =>
    api.get<{ message: string; data: any }>(`/api/leads/manager/alerts`, { params }),

  getCrmBillingAttribution: (params?: { hospitalId?: string }) =>
    api.get<{ message: string; data: any }>(`/api/leads/dashboard/crm-billing-attribution`, { params }),

  getCrmBillingMetricLeads: (params: {
    metric: string;
    staffId?: string;
    hospitalId?: string;
  }) =>
    api.get<{ message: string; data: { items: Lead[] } }>(
      `/api/leads/dashboard/crm-billing-attribution/leads`,
      { params },
    ),

  getSarvamSalesAgentDashboard: (params?: {
    from?: string;
    to?: string;
    staffId?: string;
    hospitalId?: string;
  }) =>
    api.get<{ message: string; data: SarvamSalesAgentDashboard }>(
      `/api/leads/dashboard/sarvam-agent-outcomes`,
      { params },
    ),

  getSarvamSalesAgentMetricLeads: (params: {
    metric: SarvamSalesAgentMetricKey;
    from?: string;
    to?: string;
    date?: string;
    staffId?: string;
    hospitalId?: string;
  }) =>
    api.get<{ message: string; data: { items: Lead[] } }>(
      `/api/leads/dashboard/sarvam-agent-outcomes/leads`,
      { params },
    ),
};

export type SarvamSalesAgentMetricKey =
  | "dnpRevived"
  | "dnpOpdBooked"
  | "crmOpdBooked"
  | "reminderConfirmed";

export type SarvamSalesAgentDashboard = {
  range: { fromKey: string; toKey: string; from: string; to: string };
  totals: {
    dnpRevived: number;
    dnpOpdBooked: number;
    crmOpdBooked: number;
    reminderConfirmed: number;
  };
  byDay: Array<{
    date: string;
    dnpRevived: number;
    dnpOpdBooked: number;
    crmOpdBooked: number;
    reminderConfirmed: number;
  }>;
  reps: Array<{
    rep: { id: string; name: string; email: string };
    counts: {
      dnpRevived: number;
      dnpOpdBooked: number;
      crmOpdBooked: number;
      reminderConfirmed: number;
    };
  }>;
  timeline: Array<{
    leadId: string;
    patientName: string | null;
    phone: string | null;
    metric: SarvamSalesAgentMetricKey;
    metricLabel: string;
    completedAt: string;
    repId: string | null;
    repName: string | null;
  }>;
  meta?: {
    completedCallsOnLeads: number;
    callsWithOutcome: number;
    assigneeFilter: "effective" | "all";
  };
};
